import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { login, isSignedIn, authMode } from './api/auth';
import { useMe } from './api/hooks';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { AdminApp } from './pages/admin/AdminApp';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { JoinPage } from './pages/JoinPage';
import { LibraryPage } from './pages/LibraryPage';
import { RequestAccessPage } from './pages/RequestAccessPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { SharePage } from './pages/SharePage';
import { StudioApp } from './pages/studio/StudioApp';
import { rememberSource, useLibraryState } from './state/libraryParams';
import { SessionProvider } from './state/session';

/** Browse (`/`): header, sidebar and the library. */
function BrowseApp() {
  const { state } = useLibraryState();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);

  // Each source has its own color theme.
  useEffect(() => {
    document.documentElement.dataset.source = state.source;
    rememberSource(state.source);
  }, [state.source]);

  useEffect(() => setDrawer(false), [location.pathname, location.search]);

  return (
    <>
      <Header onMenu={() => setDrawer(true)} />
      <div className="layout">
        <Sidebar open={drawer} onClose={() => setDrawer(false)} />
        <main id="app">
          <LibraryPage />
        </main>
      </div>
    </>
  );
}

/** The three areas — Browse, Studio, Admin — each with its own layout, plus album invite links. */
function AreaRoutes() {
  return (
    <Routes>
      <Route path="/studio/*" element={<StudioApp />} />
      <Route path="/admin/*" element={<AdminApp />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/upload" element={<Navigate to="/studio/upload" replace />} />
      <Route path="*" element={<BrowseApp />} />
    </Routes>
  );
}

function SignedIn() {
  const me = useMe();
  if (me.isPending)
    return (
      <div className="empty">
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  if (me.isError)
    return (
      <div className="empty">
        <h3>Couldn’t reach JBCH USA Photos</h3>
        <p>{me.error.message}</p>
        <button type="button" className="btn primary" onClick={() => void me.refetch()}>
          Try again
        </button>
      </div>
    );
  if (!me.data.user || me.data.user.status !== 'active') return <RequestAccessPage me={me.data} />;
  return (
    <SessionProvider me={me.data}>
      <AreaRoutes />
    </SessionProvider>
  );
}

function RequireSignIn() {
  const signedIn = isSignedIn();
  useEffect(() => {
    if (!signedIn && authMode === 'cognito') void login();
  }, [signedIn]);
  if (!signedIn)
    return (
      <div className="empty">
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  return <SignedIn />;
}

export function App() {
  return (
    <Routes>
      <Route path="/s/:shareId" element={<SharePage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/request-access" element={<RequireSignIn />} />
      <Route path="*" element={<RequireSignIn />} />
    </Routes>
  );
}
